import React from 'react';
import { Link } from 'react-router-dom';
import { Cpu, Zap, Code2, ShieldCheck, ArrowRight, Compass } from 'lucide-react';

const services = [
  {
    icon: Zap,
    title: 'Digital Transformation',
    tagline: 'Modernise & scale',
    description:
      'We help organisations move from legacy systems to modern, scalable architectures. Cloud migration, process automation, and end-to-end digital strategy — delivered by engineers who have done it in high-stakes environments.',
    bullets: [
      'Legacy system modernisation',
      'Cloud migration & infrastructure',
      'Process automation & workflow',
      'Change management & adoption',
    ],
    accent: 'from-blue-500 to-cyan-500',
    accentColor: '#00d4ff',
  },
  {
    icon: Cpu,
    title: 'AI Implementation',
    tagline: 'Practical AI, real results',
    description:
      'We design and build AI solutions that integrate with your existing stack — from LLM-powered products and intelligent automation to custom ML pipelines. No hype, just working software.',
    bullets: [
      'LLM integration & fine-tuning',
      'Intelligent process automation',
      'AI-powered analytics & insights',
      'Custom ML pipeline development',
    ],
    accent: 'from-violet-500 to-fuchsia-500',
    accentColor: '#a855f7',
    badge: 'High Demand',
  },
  {
    icon: Code2,
    title: 'Custom Software Development',
    tagline: 'Built to your exact needs',
    description:
      'Full-stack development for web applications, APIs, and internal tools. We work as an extension of your team or end-to-end — from architecture decisions to production deployments.',
    bullets: [
      'Web & API development',
      'Product engineering & MVP',
      'Internal tools & platforms',
      'DevOps & CI/CD setup',
    ],
    accent: 'from-emerald-500 to-teal-500',
    accentColor: '#10b981',
  },
  {
    icon: ShieldCheck,
    title: 'QA Consultancy',
    tagline: '15 years in the trenches',
    description:
      'Strategic quality engineering for teams that need more than test coverage. We connect quality metrics to business outcomes and help you build the processes, tools, and teams to sustain it.',
    bullets: [
      'QA strategy & maturity assessment',
      'Test automation architecture',
      'DORA metrics & CI/CD quality gates',
      'Team building & coaching',
    ],
    accent: 'from-orange-500 to-amber-500',
    accentColor: '#f59e0b',
  },
];

const Services = () => {
  return (
    <section id="services" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 circuit-pattern opacity-20" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center space-x-2 rounded-full px-4 py-1.5 mb-6"
            style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}>
            <span className="text-xs font-medium text-cyan-400 tracking-wide uppercase">What We Do</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
            Services built for<br />
            <span className="gradient-text">complex problems</span>
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed">
            From strategy to shipping code — we work with companies across Fintech, Payments, and Healthcare to solve real engineering challenges.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {services.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <div
                key={i}
                className="group relative rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = `${svc.accentColor}30`}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
              >
                {/* Top accent line */}
                <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${svc.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                {/* Background glow */}
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `radial-gradient(circle, ${svc.accentColor}10 0%, transparent 70%)` }} />

                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-5">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${svc.accent} p-2.5 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-full h-full text-white" />
                    </div>
                    {svc.badge && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                        style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)', color: '#c084fc' }}>
                        {svc.badge}
                      </span>
                    )}
                  </div>

                  <div className="text-xs font-medium mb-1.5" style={{ color: svc.accentColor }}>{svc.tagline}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{svc.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">{svc.description}</p>

                  <ul className="space-y-2">
                    {svc.bullets.map((b, j) => (
                      <li key={j} className="flex items-center space-x-2 text-sm text-gray-500">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: svc.accentColor }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* QE Navigator spotlight */}
        <div className="relative rounded-2xl p-8 md:p-10 overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(0,212,255,0.06) 0%, rgba(0,102,255,0.06) 100%)',
            border: '1px solid rgba(0,212,255,0.2)',
          }}>
          <div className="absolute top-0 right-0 w-72 h-72 blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 70%)' }} />
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(0,102,255,0.2))', border: '1px solid rgba(0,212,255,0.3)' }}>
                <Compass className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <div className="text-xs font-semibold text-cyan-400 uppercase tracking-wide mb-1">Our Flagship Product</div>
                <h3 className="text-xl font-bold text-white mb-1">QE Navigator</h3>
                <p className="text-gray-400 text-sm max-w-lg">
                  Built from 15 years of QA leadership — a unified dashboard that maps quality metrics to revenue impact. 
                  Jenkins, Jira, SonarQube and more, all in one place.
                </p>
              </div>
            </div>
            <Link
              to="/navigator"
              className="flex items-center space-x-2 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)',
                color: '#fff',
              }}
            >
              <span>Explore QE Navigator</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
